import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useAuth } from '@/context/AuthContext';
import { acceptInvite } from '@/lib/api/groups';
import { logEvent } from '@/lib/analytics';
import { queryKeys } from '@/lib/queryKeys';

const PENDING_KEY = 'pendingInviteCode';

/**
 * After a successful sign-in, the backend auto-redeems email-matching invites.
 * Invalidate the relevant caches and, if a `pendingInviteCode` was stashed by
 * the /join deep-link flow, redeem it explicitly. Runs once per session.
 */
export function usePostSignInInvites() {
  const { session } = useAuth();
  const token = session?.accessToken;
  const qc = useQueryClient();
  const ran = useRef(false);

  useEffect(() => {
    if (!token || ran.current) return;
    ran.current = true;
    qc.invalidateQueries({ queryKey: queryKeys.invites.mine });
    qc.invalidateQueries({ queryKey: ['groups', 'mine'] });

    (async () => {
      const code = await AsyncStorage.getItem(PENDING_KEY).catch(() => null);
      if (!code) return; // auto-redeemed email invites are recorded server-side
      try {
        await acceptInvite(token, code);
        logEvent('group_joined', { via: 'auto' });
      } catch (e: any) {
        // 409 / already accepted (auto-redeemed server-side) → fine
        if (!/already|409|accepted/i.test(e?.message || '')) {
          // keep the code so a later retry can redeem; log only
          console.warn('post-sign-in invite redeem failed:', e?.message);
          ran.current = false; // allow retry next focus
          return;
        }
      }
      await AsyncStorage.removeItem(PENDING_KEY).catch(() => {});
      qc.invalidateQueries({ queryKey: queryKeys.invites.mine });
      qc.invalidateQueries({ queryKey: ['groups', 'mine'] });
    })();
  }, [token, qc]);
}